/*
  Preorder and Sold out badges for the wholesale pages, Empire Music.

  Why this exists. The Wholesale Helper app decides whether to show a product as
  available from Shopify's `available` flag. Almost every product here is set to
  keep selling when out of stock, because the pre-order app requires that, so
  `available` is true even when the shelf is empty and dealers never see that an
  item is out of stock or on pre-order. This script reads the real inventory and
  draws the same two badges the retail side shows.

  How a label is chosen, per variant:
    stock is not tracked            no badge
    quantity above zero             no badge
    quantity 0 or less, deny        Sold out
    quantity 0 or less, continue    Preorder
  In the wholesale cart the quantity in the cart counts too (launch QA #36,
  Oct 15 2026): a line of 8 with 7 in stock is labelled "Pre-order: 1 of 8",
  the same wording the retail cart uses.

  Data comes from templates/search.em_stock.liquid in this theme. Nothing here
  depends on the Wholesale Helper or pre-order apps' own code, only on their
  markup, so if either app changes its classes the badges quietly stop appearing
  rather than breaking the page.
*/
(function () {
  'use strict';

  if (window.__emWholesaleStock) return;
  window.__emWholesaleStock = true;
  /* The storefront serves locale prefixed URLs (/fr/apps/wpdapp/...), so the
     proxy test allows an optional locale segment and every lookup is fetched
     against that same prefix. */
  var proxyMatch = document.location.pathname
    .match(/^(\/[a-z]{2}(?:-[a-zA-Z]{2})?)?\/apps\/wpdapp(?:\/|$)/);
  if (!proxyMatch) return;
  var ROOT = proxyMatch[1] || '';
  var FR = /^\/fr/i.test(ROOT);

  /* LABELS: the only wording used. These match what the retail storefront
     prints: "Preorder" and "Sold out" on product cards, and "Pre-order: 1 of 8"
     on a cart line that is partly on pre-order. The custom.stock_status
     metafield is deliberately NOT used here, because it is product level and
     goes stale; the lookup endpoint still returns it as product.s for future use. */
  var LABELS = FR ? {
    soldout: 'Épuisé',
    preorder: 'Précommande',
    partial: 'Précommande : {p} sur {t}',
    note: 'Cet article est actuellement en rupture de stock. Précommandez-le dès aujourd\'hui et soyez parmi les premiers à le recevoir dès qu\'il sera disponible.',
    delivery: 'Délai de livraison des précommandes : 2 semaines à 6 mois. Veuillez nous contacter pour plus de détails.'
  } : {
    soldout: 'Sold out',
    preorder: 'Preorder',
    partial: 'Pre-order: {p} of {t}',
    note: 'This item is currently out of stock. Pre-order today and be the first to receive your order once it\'s available.',
    delivery: 'Delivery time for pre-orders is 2 weeks to 6 months. Please contact us for details.'
  };

  var BATCH = 20;          // products per lookup request
  var CACHE_MINUTES = 10;  // how long a lookup is reused within a session
  var CACHE_KEY = 'em-wholesale-stock-v1';

  var byProduct = Object.create(null);   // product id  -> record
  var byVariant = Object.create(null);   // variant id  -> { product, variant }
  var byHandle = Object.create(null);    // handle      -> record
  var asked = Object.create(null);       // what we have already requested
  var queue = [];
  var flushTimer = null;
  var cartMap = null;                    // variant id -> { pid, qty }, from /cart.js
  var cartSig = '';                      // what the cart lines looked like at the last read

  /* ---------- cache ---------- */

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return;
      var blob = JSON.parse(raw);
      if (!blob || !blob.at || Date.now() - blob.at > CACHE_MINUTES * 60000) return;
      (blob.products || []).forEach(remember);
    } catch (e) { /* private window, blocked storage: carry on without it */ }
  }

  function writeCache() {
    try {
      var products = Object.keys(byProduct).map(function (k) { return byProduct[k]; });
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), products: products }));
    } catch (e) { /* over quota or blocked: the page still works */ }
  }

  function remember(p) {
    if (!p || !p.id) return;
    byProduct[p.id] = p;
    if (p.h) byHandle[p.h] = p;
    (p.v || []).forEach(function (v) { byVariant[v.id] = { product: p, variant: v }; });
  }

  /* ---------- lookup ---------- */

  function want(term) {
    if (asked[term]) return;
    asked[term] = true;
    queue.push(term);
    if (flushTimer) return;
    flushTimer = setTimeout(flush, 60);
  }

  function flush() {
    flushTimer = null;
    var terms = queue.splice(0, queue.length);
    for (var i = 0; i < terms.length; i += BATCH) {
      load(terms.slice(i, i + BATCH));
    }
  }

  function load(terms) {
    var q = terms.join(' OR ');
    var url = ROOT + '/search?view=em_stock&q=' + encodeURIComponent(q);
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.p) return;
        data.p.forEach(remember);
        writeCache();
        decorate();
      })
      .catch(function () { /* a failed lookup just means no badge */ });
  }

  /* ---------- the rule ---------- */

  function statusOf(product, variant) {
    if (!variant) return null;
    if (!variant.m) return null;                       // stock not tracked here
    if (typeof variant.q !== 'number' || variant.q > 0) return null;
    var kind = variant.p === 'deny' ? 'soldout' : 'preorder';
    return { kind: kind, label: LABELS[kind] };
  }

  /* A cart line also knows how many are being ordered. When that is more than
     the shelf holds and the variant keeps selling, the extra units are on
     pre-order: 8 ordered with 7 in stock reads "Pre-order: 1 of 8". */
  function statusOfLine(product, variant, qty) {
    if (!variant || !variant.m || typeof variant.q !== 'number') return null;
    if (variant.q > 0 && qty > variant.q && variant.p !== 'deny') {
      var label = LABELS.partial.replace('{p}', qty - variant.q).replace('{t}', qty);
      return { kind: 'preorder', label: label };
    }
    return statusOf(product, variant);
  }

  /* A listing tile is one tile per product and the shopper has not chosen a
     variant yet, so a badge there is only honest when every tracked variant
     is at zero. One empty size inside a product that is otherwise in stock
     belongs on the product page, not on the tile. */
  function statusOfProduct(product) {
    if (!product || !product.v || !product.v.length) return null;
    var tracked = 0, zero = 0, deny = 0, i, v;
    for (i = 0; i < product.v.length; i++) {
      v = product.v[i];
      if (!v.m) continue;
      tracked++;
      if (typeof v.q === 'number' && v.q <= 0) {
        zero++;
        if (v.p === 'deny') deny++;
      }
    }
    if (!tracked || zero !== tracked) return null;
    var kind = deny === zero ? 'soldout' : 'preorder';
    return { kind: kind, label: LABELS[kind] };
  }

  function pickVariant(product, variantId, title) {
    if (!product || !product.v || !product.v.length) return null;
    var i;
    if (variantId) {
      for (i = 0; i < product.v.length; i++) {
        if (String(product.v[i].id) === String(variantId)) return product.v[i];
      }
    }
    if (title) {
      for (i = 0; i < product.v.length; i++) {
        if (product.v[i].t === title) return product.v[i];
      }
    }
    return product.v.length === 1 ? product.v[0] : null;
  }

  /* ---------- drawing ---------- */

  function paint(host, where, status) {
    if (!host) return;
    var badge = host.querySelector(':scope > .em-stock-badge');
    if (!status) { if (badge) badge.remove(); return; }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'em-stock-badge em-stock-badge--' + where;
      if (where === 'tile') {
        var pos = window.getComputedStyle(host).position;
        if (pos === 'static') host.style.position = 'relative';
        host.insertBefore(badge, host.firstChild);
      } else {
        host.appendChild(badge);
      }
    }
    var cls = 'em-stock-badge em-stock-badge--' + where + ' em-stock-badge--' + status.kind;
    if (badge.className !== cls) badge.className = cls;
    if (badge.textContent !== status.label) badge.textContent = status.label;
  }

  function doTiles() {
    var tiles = document.querySelectorAll('.wpd-grid-item[data-product-id]');
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      var pid = tile.getAttribute('data-product-id');
      var record = byProduct[pid];
      if (!record) { want('id:"' + pid + '"'); continue; }
      var wrapper = tile.querySelector('.product-image-wrapper') || tile;
      paint(wrapper, 'tile', statusOfProduct(record));
    }
  }

  function selectedTitle() {
    var chosen = document.querySelectorAll('.wpd-product-selected-option[data-value]');
    if (!chosen.length) return '';
    var parts = [];
    for (var i = 0; i < chosen.length; i++) parts.push(chosen[i].getAttribute('data-value'));
    return parts.join(' / ');
  }

  function doProduct() {
    var match = document.location.pathname.match(/\/wholesale\/product\/([^\/?#]+)/);
    if (!match) return;
    var handle = decodeURIComponent(match[1]);
    var record = byHandle[handle];
    var price = document.querySelector('.wpd-quick-view-price');
    if (!price || !price.parentElement) return;
    if (!record) { want('handle:"' + handle + '"'); return; }
    var status = statusOf(record, pickVariant(record, null, selectedTitle()));
    paint(price.parentElement, 'inline', status);
    note(price.parentElement, status && status.kind === 'preorder');
  }

  /* The same pre-order message the retail product page shows, under the badge
     (launch QA #36, Oct 16 2026). */
  function note(host, on) {
    var el = host.querySelector(':scope > .em-stock-note');
    if (!on) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('div');
      el.className = 'em-stock-note';
      var a = document.createElement('p');
      a.textContent = LABELS.note;
      var b = document.createElement('p');
      b.className = 'em-stock-note__delivery';
      b.textContent = LABELS.delivery;
      el.appendChild(a);
      el.appendChild(b);
    }
    var badge = host.querySelector(':scope > .em-stock-badge');
    if (badge && badge.nextElementSibling !== el) badge.after(el);
    else if (!badge && el.parentNode !== host) host.appendChild(el);
  }

  function doCart() {
    var lines = document.querySelectorAll('.wpd-cart-item[data-variant-id]');
    if (!lines.length) return;
    /* The app redraws the cart when a quantity changes. Re-read /cart.js when
       the lines look different from last time, so the counts stay current. */
    var sig = cartSignature(lines);
    if (!cartMap || (cartMap !== 'loading' && sig !== cartSig)) { cartSig = sig; loadCart(); }
    if (!cartMap || cartMap === 'loading') return;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var vid = line.getAttribute('data-variant-id');
      var inCart = cartMap[vid];
      var known = byVariant[vid];
      if (!known) {
        if (inCart) want('id:"' + inCart.pid + '"');
        continue;
      }
      var host = line.querySelector('.wpd-cart-item-title-wrapper') || line.querySelector('.wpd-cart-item-title');
      paint(host, 'cart', statusOfLine(known.product, known.variant, inCart ? inCart.qty : 0));
    }
  }

  /* What the cart lines show, minus our own badges: variant, any quantity box
     values and the text. Changes when a line is added, removed or re-counted. */
  function cartSignature(lines) {
    var parts = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var values = [];
      line.querySelectorAll('input').forEach(function (input) { values.push(input.value); });
      var text = line.textContent;
      line.querySelectorAll('.em-stock-badge').forEach(function (b) { text = text.replace(b.textContent, ''); });
      parts.push(line.getAttribute('data-variant-id') + '|' + values.join(',') + '|' + text.replace(/\s+/g, ' '));
    }
    return parts.join('#');
  }

  function loadCart() {
    if (cartMap === 'loading') return;
    var previous = cartMap;
    cartMap = 'loading';
    fetch(ROOT + '/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var map = Object.create(null);
        ((cart && cart.items) || []).forEach(function (item) {
          var entry = map[item.variant_id] || (map[item.variant_id] = { pid: item.product_id, qty: 0 });
          entry.qty += item.quantity;          // the same item can sit on more than one line
        });
        cartMap = map;
        decorate();
      })
      .catch(function () {
        cartMap = previous && previous !== 'loading' ? previous : Object.create(null);
      });
  }

  /* ---------- run, and keep running: the app rerenders as you browse ---------- */

  var pass = null;
  function decorate() {
    if (pass) return;
    pass = requestAnimationFrame(function () {
      pass = null;
      try { doTiles(); doProduct(); doCart(); } catch (e) { /* never break the page */ }
    });
  }

  readCache();
  decorate();

  var settle = null;
  var observer = new MutationObserver(function () {
    clearTimeout(settle);
    settle = setTimeout(decorate, 120);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  var path = document.location.pathname;
  setInterval(function () {
    if (document.location.pathname === path) return;
    path = document.location.pathname;
    cartMap = null;
    decorate();
  }, 400);
})();

/*
  Product page pictures on the wholesale pages (launch QA, Oct 15-16 2026).

  The app shows the main picture, then every picture again stacked in a large
  grid underneath. This turns that grid into a row of thumbnails, like the retail
  product page: click or tap a thumbnail to show it in the main spot, use the
  arrows on the main picture, or swipe it on a phone. When the app changes the
  main picture itself (choosing another option), the matching thumbnail is
  highlighted.

  The pictures also stay in view while the description is read:
    - picture beside the text (desktop, and tablets when the app puts them side
      by side): the picture column sticks below the header as the text scrolls;
    - picture above the text (phones): once the big picture has scrolled away, a
      compact picture bar with the same arrows and thumbnails sticks below the
      header; tapping its picture scrolls back up to the big one.
  Which one applies is measured from the page, not from a fixed screen width, so
  it follows the app's own layout. The theme header hides on scroll down and
  comes back on scroll up; both follow it.

  Only the app's markup is used; if it changes, the page simply falls back to the
  app's own layout.
*/
(function () {
  'use strict';

  if (window.__emWholesaleGallery) return;
  if (!/^(\/[a-z]{2}(?:-[a-zA-Z]{2})?)?\/apps\/wpdapp(?:\/|$)/.test(document.location.pathname)) return;
  window.__emWholesaleGallery = true;

  var GAP = 16;  // space between the header (or top of the screen) and a sticky picture
  var ARROW_LEFT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ARROW_RIGHT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var active = null;   // the gallery on the page now: { wrap, main, info, bar, place }

  function same(a, b) {
    return (a || '').split('?')[0].replace(/^https?:/, '') === (b || '').split('?')[0].replace(/^https?:/, '');
  }

  function navButton(kind, label, svg, onClick) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'em-gallery__nav em-gallery__nav--' + kind;
    button.setAttribute('aria-label', label);
    button.innerHTML = svg;
    button.addEventListener('click', onClick);
    return button;
  }

  function swipe(el, step) {
    var startX = null, startY = null;
    el.addEventListener('touchstart', function (event) {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', function (event) {
      if (startX === null) return;
      var dx = event.changedTouches[0].clientX - startX;
      var dy = event.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  /* Where the visible part of the theme header ends, so sticky things sit under it. */
  function headerBottom() {
    var header = document.querySelector('sticky-header, .section-header');
    if (!header) return 0;
    var r = header.getBoundingClientRect();
    return r.bottom > 0 ? r.bottom : 0;
  }

  function setUp() {
    var wrap = document.getElementById('wpd_product_quick_view_image_wrapper');
    var grid = document.getElementById('wpd_product_image_gallery_wrapper');
    var info = document.getElementById('wpd_product_quick_view_product_info_wrapper');
    if (!wrap || !grid) { teardownFloat(); return; }
    var main = wrap.querySelector('img.wpd-product-quick-view-images');
    var thumbs = Array.prototype.slice.call(grid.querySelectorAll('img.wpd-product-sub-images'));
    if (!main || !thumbs.length) return;
    if (grid.emGallery && grid.emGallery.count === thumbs.length && grid.emGallery.main === main) {
      grid.emGallery.sync();
      return;
    }

    teardownFloat();
    wrap.classList.add('em-gallery');
    grid.classList.add('em-gallery__thumbs');
    grid.classList.toggle('em-gallery__thumbs--single', thumbs.length < 2);
    main.classList.add('em-gallery__main');

    var overlay = wrap.querySelector(':scope > .em-gallery__overlay');
    if (overlay) overlay.remove();
    overlay = document.createElement('div');
    overlay.className = 'em-gallery__overlay';
    wrap.appendChild(overlay);

    var current = 0;
    var many = thumbs.length > 1;

    /* The compact bar used when the picture sits above the text. */
    var bar = document.createElement('div');
    bar.className = 'em-gallery-float';
    bar.setAttribute('aria-hidden', 'true');
    var barStage = document.createElement('div');
    barStage.className = 'em-gallery-float__stage';
    var barImg = document.createElement('img');
    barImg.className = 'em-gallery-float__img';
    barImg.alt = main.getAttribute('alt') || '';
    barImg.title = 'Back to the full-size pictures';
    barImg.addEventListener('click', function () {
      wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    barStage.appendChild(barImg);
    var barThumbs = document.createElement('div');
    barThumbs.className = 'em-gallery-float__thumbs';
    var barThumbImgs = thumbs.map(function (t, i) {
      var img = document.createElement('img');
      img.className = 'em-gallery-float__thumb';
      img.src = t.getAttribute('src');
      img.alt = '';
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', 'Show picture ' + (i + 1) + ' of ' + thumbs.length);
      img.addEventListener('click', function () { show(i, true); });
      barThumbs.appendChild(img);
      return img;
    });
    if (many) {
      barStage.appendChild(navButton('prev', 'Previous picture', ARROW_LEFT, function () { show(current - 1, true); }));
      barStage.appendChild(navButton('next', 'Next picture', ARROW_RIGHT, function () { show(current + 1, true); }));
      swipe(barImg, function (d) { show(current + d, true); });
    }
    bar.appendChild(barStage);
    if (many) bar.appendChild(barThumbs);
    document.body.appendChild(bar);

    function show(index, scroll) {
      if (index < 0) index = thumbs.length - 1;
      if (index >= thumbs.length) index = 0;
      current = index;
      if (!same(main.getAttribute('src'), thumbs[index].getAttribute('src'))) {
        main.setAttribute('src', thumbs[index].getAttribute('src'));
      }
      mark(scroll);
    }

    function mark(scroll) {
      thumbs.forEach(function (img, i) {
        var on = i === current;
        img.classList.toggle('is-active', on);
        img.setAttribute('aria-current', on ? 'true' : 'false');
        barThumbImgs[i].classList.toggle('is-active', on);
      });
      if (!same(barImg.getAttribute('src'), main.getAttribute('src'))) barImg.src = main.getAttribute('src');
      if (scroll && thumbs[current]) {
        var t = thumbs[current];
        grid.scrollTo({ left: Math.max(0, t.offsetLeft - (grid.clientWidth - t.offsetWidth) / 2), behavior: 'smooth' });
        var bt = barThumbImgs[current];
        barThumbs.scrollTo({ left: Math.max(0, bt.offsetLeft - (barThumbs.clientWidth - bt.offsetWidth) / 2), behavior: 'smooth' });
      }
    }

    function sync() {
      for (var i = 0; i < thumbs.length; i++) {
        if (same(thumbs[i].getAttribute('src'), main.getAttribute('src'))) { current = i; break; }
      }
      mark(false);
      place();
    }

    /* Keep the arrows over the main picture, whatever its size. */
    function place() {
      overlay.style.top = main.offsetTop + 'px';
      overlay.style.left = main.offsetLeft + 'px';
      overlay.style.width = main.offsetWidth + 'px';
      overlay.style.height = main.offsetHeight + 'px';
    }

    thumbs.forEach(function (img, i) {
      img.classList.add('em-gallery__thumb');
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', 'Show picture ' + (i + 1) + ' of ' + thumbs.length);
      img.addEventListener('click', function () { show(i, true); });
      img.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(i, true); }
      });
    });

    if (many) {
      overlay.appendChild(navButton('prev', 'Previous picture', ARROW_LEFT, function () { show(current - 1, true); }));
      overlay.appendChild(navButton('next', 'Next picture', ARROW_RIGHT, function () { show(current + 1, true); }));
      swipe(main, function (d) { show(current + d, true); });
    }

    new MutationObserver(sync).observe(main, { attributes: true, attributeFilter: ['src'] });
    main.addEventListener('load', function () { place(); update(); });

    active = { wrap: wrap, main: main, info: info, bar: bar, place: place };
    grid.emGallery = { count: thumbs.length, main: main, sync: sync };
    sync();
    update();
  }

  function teardownFloat() {
    if (active && active.bar) active.bar.remove();
    active = null;
  }

  /* Decide sticky column or compact bar, and keep both under the header. */
  var ticking = false;
  function update() {
    ticking = false;
    if (!active || !document.body.contains(active.wrap)) { teardownFloat(); return; }
    var wrap = active.wrap, main = active.main, info = active.info, bar = active.bar;
    var top = headerBottom() + GAP;
    document.documentElement.style.setProperty('--em-stick-top', top + 'px');
    document.documentElement.style.setProperty('--em-bar-top', headerBottom() + 'px');
    var header = document.querySelector('sticky-header, .section-header');
    document.documentElement.style.setProperty('--em-header-h', (header ? header.offsetHeight : 0) + 'px');

    var beside = false;
    if (info) {
      var w = wrap.getBoundingClientRect(), n = info.getBoundingClientRect();
      beside = w.right <= n.left + 1 || n.right <= w.left + 1;
    }
    if (wrap.classList.contains('em-gallery--sticky') !== beside) {
      wrap.classList.toggle('em-gallery--sticky', beside);
    }
    active.place();   // the picture can change size with the layout or the header

    var showBar = false;
    if (!beside) {
      var m = main.getBoundingClientRect();
      var end = (wrap.parentElement || wrap).getBoundingClientRect().bottom;
      showBar = m.bottom < headerBottom() + 40 && end > headerBottom() + bar.offsetHeight + 80;
    }
    bar.classList.toggle('is-visible', showBar);
    bar.setAttribute('aria-hidden', showBar ? 'false' : 'true');
  }
  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', function () {
    requestUpdate();
    var grid = document.getElementById('wpd_product_image_gallery_wrapper');
    if (grid && grid.emGallery) grid.emGallery.sync();
  });

  var timer = null;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(function () { setUp(); requestUpdate(); }, 150);
  }).observe(document.body, { childList: true, subtree: true });
  setUp();
})();
